import { useMutation, useQuery, useQueryClient } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "src/App";
import {
  useNotificationStream,
  isNotificationStreamHealthy,
  useSSEFallbackInvalidation,
} from "src/reusable/hooks/useNotificationStream";

// Statuses a run never leaves — polling stops once one is reached.
const TERMINAL_RUN_STATUSES = ["success", "failed", "skipped", "cancelled"];

const getJob = (id) => {
  return axios.get(nuclearn_api_url + "/jobs/" + id).then((response) => {
    return response.data;
  });
};

export const useGetJob = (id) => {
  return useQuery(["job", id], () => getJob(id), {
    refetchOnWindowFocus: false,
    enabled: !!id,
  });
};

// this used to be named useGetJobs, probably needs to be replaced with useGetJobs as it is currently defined
export const fetchJob = (id) => {
  return axios.get(nuclearn_api_url + "/jobs/" + id).then((response) => {
    return response.data;
  });
};

export const downloadJob = (id, callback) => {
  fetchJob(id)
    .then((data) => {
      let json = JSON.stringify(data, null, 2); // Convert JSON to string, with indentation
      let blob = new Blob([json], { type: "application/json" });
      let url = URL.createObjectURL(blob);
      let aTag = document.createElement("a");
      aTag.download = data.name + "-job.json"; // The filename for download
      aTag.href = url;
      document.body.appendChild(aTag);
      aTag.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(aTag);
    })
    .finally(() => {
      if (callback) {
        callback();
      }
    });
};

const getJobLogs = (id) => {
  return axios
    .get(nuclearn_api_url + "/jobs/runs/" + id + "/logs")
    .then((response) => {
      return response.data;
    });
};

export const useGetJobLogs = (id, enabled) => {
  return useQuery(["JobLogs", id], () => getJobLogs(id), {
    refetchOnWindowFocus: false,
    enabled: !!id && enabled,
    retry: false,
  });
};

const getJobsList = (showSystemJobsOnly = false) => {
  return axios
    .get(nuclearn_api_url + `/jobs/all?show_system_jobs=${showSystemJobsOnly}`)
    .then((response) => {
      return response.data.sort(function (a, b) {
        // sourcery skip: use-braces
        if (a.id < b.id) return -1;
        if (a.id > b.id) return 1;
        return 0;
      });
    });
};

export const useJobsList = (
  showSystemJobsOnly = false,
  refetchInterval = false,
) => {
  return useQuery(
    ["JobsList", showSystemJobsOnly],
    () => getJobsList(showSystemJobsOnly),
    {
      refetchOnWindowFocus: false,
      refetchInterval,
    },
  );
};

const createJob = (job) => {
  return axios.put(nuclearn_api_url + "/jobs", job).then((response) => {
    return response.data;
  });
};

export const useCreateJob = () => {
  const queryClient = useQueryClient();

  return useMutation(createJob, {
    onSuccess: () => {
      queryClient.invalidateQueries("JobsList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
};

export const updateJob = (job, callback) => {
  const payload = {
    name: job.name,
    config: job.config,
    failure_notification_emails: job.failure_notification_emails,
  };
  if (job.skip_if_running !== undefined) {
    payload.skip_if_running = job.skip_if_running;
  }
  return axios
    .patch(nuclearn_api_url + "/jobs/" + job.id, payload)
    .then((response) => {
      if (callback) {
        callback(response.data);
      }
      return response.data;
    });
};

export const useUpdateJob = () => {
  const queryClient = useQueryClient();

  return useMutation(updateJob, {
    onSuccess: (data, vars) => {
      const { id } = vars;
      queryClient.invalidateQueries(["job", id]);
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
};

const runJob = (jobId) => {
  return axios
    .post(`${nuclearn_api_url}/jobs/${jobId}/run`)
    .then((response) => {
      return response.data;
    });
};

export const useRunJob = (setJobRunId) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(runJob, {
    onSuccess: (result) => {
      queryClient.invalidateQueries(["JobRunStatus", result.job_run_id]);
      // Refresh the Jobs list immediately so the new run shows up in
      // row.last_run; otherwise the Status / Last Run columns stay stale
      // until the next 30s list refetch and JobStatusCell never sees the
      // run as in-progress (so SSE/3s polling never activates either).
      queryClient.invalidateQueries("JobsList");
      setJobRunId(result.job_run_id);
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const cancelJobRun = (jobRunId) => {
  return axios
    .post(`${nuclearn_api_url}/jobs/runs/${jobRunId}/cancel`)
    .then((response) => {
      return response.data;
    });
};

export const useCancelJobRun = () => {
  const queryClient = useQueryClient();

  return useMutation(cancelJobRun, {
    onSuccess: (result) => {
      queryClient.invalidateQueries(["JobRunStatus", result.job_run_id]);
      queryClient.invalidateQueries("JobsList");
      queryClient.invalidateQueries("JobRuns");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
};

const jobRunStatus = (jobRunId) => {
  return axios
    .get(`${nuclearn_api_url}/jobs/runs/${jobRunId}/status`)
    .then((response) => {
      return response.data;
    });
};

// retrieve the job run status — SSE for real-time updates, polling as fallback
export const useJobRunStatus = (jobRunId, interval) => {
  const queryClient = useQueryClient();
  const queryKey = ["JobRunStatus", jobRunId];

  const query = useQuery(queryKey, () => jobRunStatus(jobRunId), {
    refetchOnWindowFocus: true,
    enabled: !!jobRunId,
    refetchInterval: (data) => {
      if (data && TERMINAL_RUN_STATUSES.includes(data.status)) {
        return false;
      }
      // SSE handles updates when healthy; fall back to polling otherwise
      if (isNotificationStreamHealthy() && jobRunId) {
        return false;
      }
      return interval;
    },
  });

  // Subscribe to SSE updates for this job run
  const streamKey = jobRunId ? `notify:job_run:${jobRunId}:status` : null;

  useNotificationStream(streamKey, "job_run_status", (data) => {
    queryClient.setQueryData(queryKey, (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        status: data.status !== undefined ? data.status : prev.status,
        message: data.message !== undefined ? data.message : prev.message,
        finish_date:
          data.finish_date !== undefined ? data.finish_date : prev.finish_date,
      };
    });
  });

  useSSEFallbackInvalidation(queryClient, queryKey, !!jobRunId);

  return query;
};

const jobRuns = (id) => {
  return axios.get(`${nuclearn_api_url}/jobs/${id}/runs`).then((response) => {
    return response.data;
  });
};

export const useJobRuns = (id, refetchInterval = false) => {
  return useQuery(["JobRuns", id], () => jobRuns(id), {
    refetchOnWindowFocus: false,
    enabled: !!id,
    refetchInterval: refetchInterval,
  });
};
const getJobSchedule = (id) => {
  return axios
    .get(nuclearn_api_url + "/schedules/job-schedule/" + id)
    .then((response) => {
      return response.data;
    });
};

export const useGetJobSchedule = (id) => {
  return useQuery(["JobsScheduleList", id], () => getJobSchedule(id), {
    refetchOnWindowFocus: false,
    enabled: !!id,
  });
};

const createJobSchedule = (job) => {
  //console.log(job);
  return axios
    .put(nuclearn_api_url + "/schedules/job-schedule", job)
    .then((response) => {
      return response.data;
    });
};

export const useJobScheduleCreate = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(createJobSchedule, {
    onSuccess: () => {
      queryClient.invalidateQueries("JobsScheduleList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const deleteJobSchedule = (job) => {
  return axios
    .delete(
      nuclearn_api_url +
        "/schedules/job-schedule-by-id?job_id=" +
        job.id +
        "&schedule_job_id=" +
        job.schedule_id,
    )
    .then(() => {
      //console.log(response);
      return job.id;
    });
};

export const useDeleteJobSchedule = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteJobSchedule, {
    onSuccess: () => {
      queryClient.invalidateQueries("JobsScheduleList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const deleteJob = (job) => {
  return axios.delete(nuclearn_api_url + "/jobs/" + job.id).then(() => {
    //console.log(response);
    return job.id;
  });
};

export const useDeleteJob = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteJob, {
    onSuccess: () => {
      queryClient.invalidateQueries("JobsList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};
